import { useNavigate, useParams } from '@tanstack/react-router';
import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLotByCode } from '../api/hooks';
import { OfflineBanner, TopBar } from '../components/chrome';
import { Card, Loading, Muted, Screen, SecondaryButton } from '../components/ui';

/**
 * The QR landing page.
 *
 * This route — `/l/<code>` on whatever origin the backend's
 * PUBLIC_WEB_BASE_URL points at — is what the printed code at the lot
 * entrance actually encodes, so it is most drivers' first contact with the
 * product: no app, no account, standing at a barrier. It resolves the code
 * and forwards straight into that lot's flow.
 */
export function LotLandingPage() {
  const { t } = useTranslation();
  const { code } = useParams({ from: '/l/$code' });
  const navigate = useNavigate();
  const lot = useLotByCode(code);
  // The failed code must survive into the editable field: fixing a typo costs
  // one character, never a restart from an empty home page (review 4.2).
  const [draft, setDraft] = useState(code);

  useEffect(() => {
    if (lot.data) {
      void navigate({ to: '/lot/$lotId', params: { lotId: lot.data.id }, replace: true });
    }
  }, [lot.data, navigate]);

  // Retry must resubmit what is in the field, not reload the page: an edited
  // code re-resolves under a fresh URL, an unchanged one refetches in place.
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (draft === code) void lot.refetch();
    else void navigate({ to: '/l/$code', params: { code: draft }, replace: true });
  };

  if (lot.isError) {
    return (
      <>
        <TopBar title={t('home.codeTitle')} />
        <OfflineBanner />
        <Screen>
          <div className="flex flex-col gap-1 pt-2">
            {/* The copy must quote the exact code that failed, so the driver
                compares it against the entrance sign without re-typing it. */}
            <p className="text-[17px] font-semibold">{t('landing.notFound', { code })}</p>
            <Muted>{t('landing.notFoundHint')}</Muted>
          </div>

          <Card>
            {/* Same responsive constraint as the home code entry (ticket 01):
                stacked below 375px, one row from 375px up — the input must
                shrink (min-w-0 beats its intrinsic min-width) and the button
                must not wrap its label in EN or ZH. */}
            <form onSubmit={submit} className="flex flex-col gap-2 xs:flex-row">
              <input
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={draft}
                onChange={(event) => setDraft(event.target.value.replace(/\D/g, ''))}
                placeholder="4821"
                aria-label={t('home.codeLabel')}
                className="num min-h-[52px] w-full min-w-0 rounded-lg border-[1.5px] border-ink px-4 text-xl tracking-[4px] outline-none xs:flex-1"
              />
              <button
                type="submit"
                disabled={draft.length < 4 || lot.isFetching}
                className="min-h-[52px] w-full whitespace-nowrap rounded-lg bg-ink px-5 text-[15px] font-semibold text-white disabled:opacity-40 xs:w-auto xs:shrink-0"
              >
                {lot.isFetching ? `${t('common.loading')}…` : t('common.retry')}
              </button>
            </form>
          </Card>

          {/* No scanner exists on this page — the entrance QR points AT this
              site — so the way out is the same honest camera-gesture guidance
              as the home entry (8aae052), never a fake scan button. */}
          <Card>
            <p className="text-[15px] font-semibold">{t('home.scanTitle')}</p>
            <Muted>{t('home.scanHint')}</Muted>
          </Card>

          <SecondaryButton label={t('landing.nearby')} onClick={() => void navigate({ to: '/' })} />
        </Screen>
      </>
    );
  }

  return (
    <>
      <TopBar title={t('landing.opening')} back={false} />
      <OfflineBanner />
      <Screen>
        <Loading label={t('landing.looking', { code })} />
      </Screen>
    </>
  );
}
